import { useMemo, useEffect, useCallback, useContext } from "react"
import { useDispatch } from "react-redux"
import { getSignedUrl } from "../Actions/API/Data/DataActions"
import { FetchState, hasValue } from "../Models/Client/FetchState"
import { useAppState } from "./useReduxState"
import { Result } from "../Models/Utility/Result"
import { extractDataUrlPath, isExpiredSignedUrl } from "../Models/API/DataModel"
import { useSignedUrlExpiryMonitoring } from "./useSignedUrlExpiryMonitoring"
import { DataAccessContext } from "../Context/DataAccessContext"

/**
 * Returns signed url if signing is necessary.
 * This hook attempts to refresh signed url before it expires.
 *
 * @param originalUrl url to sign (starting with data://), or just normal url
 */
export function useSignedUrl(originalUrl?: string): FetchState<string> {
    const dataAccess = useContext(DataAccessContext)
    const pathToSign =
        originalUrl === undefined ? undefined : extractDataUrlPath(originalUrl)
    const dispatch = useDispatch()
    const signedUrl = useAppState(state =>
        pathToSign === undefined
            ? undefined
            : state.api.data.signedUrls[pathToSign]
    )
    const handleRefresh = useCallback(() => {
        if (pathToSign !== undefined) {
            dispatch(getSignedUrl(pathToSign, dataAccess))
        }
    }, [dispatch, pathToSign, dataAccess])
    const isExpired = useMemo(
        () => hasValue(signedUrl) && isExpiredSignedUrl(signedUrl.value),
        [signedUrl]
    )

    // if signed url isn't fetched, trigger fetch
    useEffect(() => {
        if (
            pathToSign !== undefined &&
            (signedUrl === undefined || isExpired)
        ) {
            dispatch(getSignedUrl(pathToSign, dataAccess))
        }
    }, [dispatch, pathToSign, signedUrl, dataAccess, isExpired])

    // refresh fetched signed url before it expires
    useSignedUrlExpiryMonitoring(
        hasValue(signedUrl) ? [signedUrl.value] : [],
        handleRefresh
    )

    return useMemo(() => {
        if (pathToSign === undefined) {
            return originalUrl === undefined
                ? undefined
                : Result.ok(originalUrl)
        }

        return signedUrl === undefined || isExpired ? undefined : signedUrl
    }, [pathToSign, signedUrl, isExpired, originalUrl])
}

interface SignedMap {
    [index: string]: FetchState<string>
}

export function useSignedUrls(originalUrls: string[]): SignedMap {
    const dispatch = useDispatch()
    const dataAccess = useContext(DataAccessContext)
    const pathsToSign = useMemo(
        () => originalUrls.map(originalUrl => extractDataUrlPath(originalUrl)),
        [originalUrls]
    )
    const signedUrls = useAppState(state => state.api.data.signedUrls)
    const handleRefresh = useCallback(() => {
        pathsToSign.forEach(pathToSign => {
            if (pathToSign) dispatch(getSignedUrl(pathToSign, dataAccess))
        })
    }, [dispatch, pathsToSign, dataAccess])

    // if signed url isn't fetched, trigger fetch
    useEffect(() => {
        pathsToSign.forEach(pathToSign => {
            if (pathToSign) {
                const signedUrl = signedUrls[pathToSign]
                if (
                    signedUrl === undefined ||
                    (hasValue(signedUrl) && isExpiredSignedUrl(signedUrl.value))
                )
                    dispatch(getSignedUrl(pathToSign, dataAccess))
            }
        })
    }, [dispatch, pathsToSign, signedUrls, dataAccess])

    // refresh fetched signed url before it expires
    useSignedUrlExpiryMonitoring(
        Object.values(signedUrls).flatMap(urlFetch =>
            hasValue(urlFetch) ? [urlFetch.value] : []
        ),
        handleRefresh
    )

    return useMemo(() => {
        return originalUrls.reduce<SignedMap>((obj, originalUrl, index) => {
            const pathToSign = pathsToSign[index]
            if (pathToSign === undefined) {
                obj[originalUrl] =
                    originalUrl === undefined
                        ? undefined
                        : Result.ok(originalUrl)
            } else {
                const urlFetch = signedUrls[pathToSign]

                obj[originalUrl] =
                    urlFetch === undefined ||
                    (hasValue(urlFetch) && isExpiredSignedUrl(urlFetch.value))
                        ? undefined
                        : urlFetch
            }
            return obj
        }, {})
    }, [pathsToSign, originalUrls, signedUrls])
}
